/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce, take } from 'rxjs/operators';

import { TSortDirection } from '../classes/grid-page-info';
import { GQLResponse, IGQLParams } from '../interfaces/graphql.interface';

const addressKeys = ['city', 'line1', 'line2', 'state', 'zipcode'] as const;
export type TAddressFilterKeys = (typeof addressKeys)[number];

const isAddressKey = (x: any): x is TAddressFilterKeys => {
  return addressKeys.includes(x as TAddressFilterKeys);
};

@Injectable({
  providedIn: 'root',
})
export class HelperGQLService {
  cleanFilterObject(
    filterValue: ArrayLike<unknown> | Record<string, unknown>,
  ): any {
    for (const [k, v] of Object.entries(filterValue)) {
      if (v && typeof v === 'object') {
        this.cleanFilterObject(v as Record<string, unknown>);
      }
      if (
        (v && typeof v === 'object' && Object.keys(v).length === 0) ||
        v === null ||
        v === undefined ||
        v === ''
      ) {
        if (Array.isArray(filterValue)) {
          filterValue.splice(Number(k), 1);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete filterValue[k];
        }
      }
    }
    return Object.keys(filterValue).length > 0 ? filterValue : null;
  }

  buildFilters(filtersObject: object) {
    const filters: Record<string, any> = {};

    Object.keys(filtersObject).map((key) => {
      const value = filtersObject[key];

      if (typeof value === 'string' && value) {
        if (isAddressKey(key)) {
          Object.assign(filters, this.buildAddressFilter(key, value));
        } else {
          filters[key] = { startsWith: value };
        }
      }

      if (typeof value === 'number' && value > 0) {
        filters[key] = { eq: value };
      }

      if (typeof value === 'boolean') {
        filters[key] = { eq: value };
      }
    });

    return Object.keys(filters).length > 0 ? filters : null;
  }

  buildAddressOrder(key: TAddressFilterKeys, direction: TSortDirection) {
    const filters: { address: Record<string, any> } = { address: {} };
    filters.address[key] = direction;
    return filters;
  }

  buildAddressFilter(key: TAddressFilterKeys, value: string) {
    const filter: { address: Record<string, { eq: string }> } = { address: {} };
    filter.address[key] = { eq: value };
    return filter;
  }

  getAllPages<TResult, TFilters>(
    func: (
      fparams: IGQLParams<TFilters>,
      groupIDParams: any,
    ) => Observable<GQLResponse<TResult>>,
    params: IGQLParams<TFilters>,
    groupIDParams: any,
  ): Observable<TResult[]> {
    return func(params, groupIDParams).pipe(
      take(1),
      expand((result) => {
        return result.pageInfo.hasNextPage
          ? func(
              { ...params, after: result.pageInfo.endCursor },
              groupIDParams,
            ).pipe(take(1))
          : EMPTY;
      }),
      reduce((acc: TResult[], res: GQLResponse<TResult>) => {
        return acc.concat(res.nodes);
      }, []),
    );
  }
}

export function TransformDate(
  _target: any,
  _key: string,
  descriptor: PropertyDescriptor,
) {
  const originalMethod = descriptor.value as (...args: any[]) => any; // Explicit typing of original method
  descriptor.value = function (...data: any[]) {
    const r = Reflect.apply(originalMethod, this, [...data]).pipe(
      map((obj: object) => {
        const oTransformed = stringToDate(obj);
        const newObj = Object.assign({}, obj, oTransformed);
        return newObj;
      }),
    );
    return r;
  };
  return descriptor;
}

const isDate = (s: string) => {
  return DateTime.fromISO(s).isValid;
};

function stringToDate(obj?: object) {
  const datesObj: Record<string, Date> = {};
  const obj2 = obj ?? {};

  Object.keys(obj2)
    .filter((key) => {
      return (
        obj2[key] &&
        typeof obj2[key] === 'string' &&
        key.toLowerCase().includes('date') &&
        isDate(obj2[key])
      );
    })
    .map((key) => {
      datesObj[key] = DateTime.fromISO(obj2[key] as string).toJSDate();
    });

  return datesObj;
}
